<template>
    <div class="jgile-table" v-loading="busy">
        <with-data
            :debounce-ms="debounce"
            :data-test="id"
            :history="history"
            :query-string-defaults="{}"
            :query.sync="proxy"
            :source="getTableData"
            ref="withData"
        >
            <div
                slot-scope="{ result, isLoaded, isLoading, queryString }"
                :class="{
                    'table-empty': !Number(result.total),
                    'table-loading': isLoading,
                    'table-loaded': isLoaded,
                }"
            >
                <portal :to="id + 'Search'">
                    <data-filter v-model="computedSearch">
                        <input
                            :placeholder="searchPlaceholder"
                            :value="computedSearch"
                            @input="set"
                            class="form-control"
                            slot-scope="{ set }"
                            type="text"
                        />
                    </data-filter>
                </portal>
                <portal :to="id + 'Paginator'">
                    <data-filter v-model="query.page">
                        <b-pagination
                            size="md"
                            slot-scope="{ set }"
                            @change="set"
                            :hide-goto-end-buttons="true"
                            :total-rows="Number(result.total)"
                            :value="_defaultTo(query.page, 1)"
                            :per-page="Number(result.perPage)"
                        />
                    </data-filter>
                </portal>
                <portal :to="id + 'PerPage'">
                    <data-filter v-model="query.perPage">
                        <t-select
                            :allow-empty="false"
                            :options="perPage"
                            :value="Number(result.perPage)"
                            @input="set"
                            :data-test="id + '-per-page-select'"
                            slot-scope="{ set }"
                        ></t-select>
                    </data-filter>
                </portal>
                <slot
                    :columns="tableColumns"
                    :is-loaded="isLoaded"
                    :is-loading="isLoading"
                    :query="query"
                    :query-string="queryString"
                    :result="result"
                    name="beforeTable"
                ></slot>
                <table :class="tableClass" v-if="!(result.total === 0 && isLoaded && hideEmpty)">
                    <thead>
                    <tr>
                        <th class="t-table-select-all" v-if="selectable">
                            <b-form-checkbox
                                v-model="selectAll"
                                name="tableSelectAll"
                                @change="toggleAll"
                                v-b-tooltip.hover.left
                                title="Select All"
                            ></b-form-checkbox>
                        </th>
                        <th
                            :class="column.classes"
                            :key="column.key"
                            :style="column.style"
                            :colspan="column.colspan"
                            v-for="column in tableColumns"
                        >
                            <template v-if="column.sortable">
                                <data-sort-toggle :for="column.key" v-model="query.sort">
                                    <template slot-scope="{ isSortedAsc, isSortedDesc }">
                                        {{ column.label }}
                                        <span v-show="isSortedAsc" class="header-icon">
                                                <i class="fas fa-fw fa-sort-up"></i>
                                            </span>
                                        <span v-show="isSortedDesc" class="header-icon">
                                                <i class="fas fa-fw fa-sort-down"></i>
                                            </span>
                                        <span v-show="!isSortedDesc && !isSortedAsc" class="header-icon">
                                                <i class="fas fa-fw fa-sort"></i>
                                            </span>
                                    </template>
                                </data-sort-toggle>
                            </template>
                            <template v-else>
                                <span class="column-title">{{ column.label }}</span>
                            </template>
                        </th>
                    </tr>
                    </thead>
                    <template v-for="(item, item_key) in result.data">
                        <slot :columns="tableColumns" :query="query" :row-key="item_key" name="beforeRow"></slot>
                        <slot
                            :columns="tableColumns"
                            :query="query"
                            :row="item"
                            :row-key="item_key"
                            :is-loaded="isLoaded"
                            :is-loading="isLoading"
                            name="row"
                        >
                            <tr :data-test="'table-row-' + item_key">
                                <td class="t-table-select-all" v-if="selectable">
                                    <b-form-checkbox
                                        :value="_get(item, _get(selectableConfig, 'attribute'))"
                                        name="tableSelected"
                                        v-model="selection"
                                    ></b-form-checkbox>
                                </td>
                                <td v-for="column in tableColumns">
                                    <slot :columns="tableColumns" :name="column.key" :query="query" :row="item"
                                          :row-key="item_key">
                                        {{ item[column.key] }}
                                    </slot>
                                </td>
                            </tr>
                            <template v-if="result.total === 0">
                                <slot name="noResults">
                                    <tr>
                                        <td :colspan="tableColumns.length">
                                            No Results
                                        </td>
                                    </tr>
                                </slot>
                            </template>
                        </slot>
                    </template>
                    <slot
                        :columns="tableColumns"
                        :is-loaded="isLoaded"
                        :is-loading="isLoading"
                        :query="query"
                        :query-string="queryString"
                        :result="result"
                        name="afterBody"
                    ></slot>
                </table>
                <slot
                    :columns="tableColumns"
                    :is-loaded="isLoaded"
                    :is-loading="isLoading"
                    :query="query"
                    :query-string="queryString"
                    :result="result"
                    name="afterTable"
                ></slot>
                <div class="row" v-if="pagination">
                    <div class="col mt-2">
                        <data-filter v-model="query.page">
                            <b-pagination
                                :hide-goto-end-buttons="true"
                                :per-page="Number(result.perPage)"
                                :total-rows="Number(result.total)"
                                :value="query.page"
                                @change="set"
                                size="md"
                                slot-scope="{ set }"
                            />
                        </data-filter>
                    </div>
                </div>
            </div>
        </with-data>
    </div>
</template>

<script>
import TableData from './TableData';
import GridMixins from '../mixins/grids';

export default {
    mixins: [GridMixins],
    components: { TableData },
    props: {
        id: {
            type: String,
            default: 'table',
        },
    },
};
</script>
